import { NolimitApplication } from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import { ScreenBounds } from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import { Device } from "@nolimitcity/slot-launcher/bin/interfaces/APIOptions";
import { gsap } from "gsap";
import { AnimatedSprite, Container, Graphics, Sprite, Texture } from "pixi.js";

import { AbstractReel } from "../../../abstracts/elements/AbstractReel";
import { AbstractSymbol } from "../../../abstracts/elements/AbstractSymbol";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { AbstractSpine } from "../../../abstracts/graphics/AbstractSpine";
import { AbstractView } from "../../../abstracts/modules/AbstractView";
import { generateScaledSprite } from "../../../abstracts/utils/GraphicUtils";
import { getRandomBetween } from "../../../abstracts/utils/JSUtils";
import { playSound, playSoundOnce } from "../../../abstracts/utils/SoundUtils";
import {
    davidPanningReelStop,
    devDebugConfig,
    displayConfig,
    fishMultipliersMap,
    reelsConfig,
    symbolConfig,
} from "../../UCConfig";
import { UgliestCatch } from "../../UgliestCatch";
import { UCSoundNames, UCSpineNames } from "../../assetsLoader/UCAssetsNames";
import { UCSpecialSymbolNames, UCSymbolNames } from "../../types/UCEnums";
import { UCFreespinMultiplier } from "./UCFreespinMultiplier";
import UCReel from "./UCReel";
import { UCReelBlocker } from "./UCReelBlocker";
import { UCBigFishSymbol } from "./symbols/UCBigFishSymbol";
import { UCMysterySymbol } from "./symbols/UCMysterySymbol";
import { UCSymbol } from "./symbols/UCSymbol";
import { getSymbolFromPool, returnSymbolToPool } from "./symbols/UCSymbolsPool";
import { UCWildSymbol } from "./symbols/UCWildSymbol";

export class UCReelsView extends AbstractView {
    private readonly currentReels: UCReel[];

    private separatorContainer: Container;
    private blockersContainer: Container;
    private xBetFrame: AnimatedSprite;
    private fsMultiplier: UCFreespinMultiplier;
    private catchFishSoundsList: UCSoundNames[] = [
        UCSoundNames.WILD_CATCH_FISH_01,
        UCSoundNames.WILD_CATCH_FISH_02,
        UCSoundNames.WILD_CATCH_FISH_03,
    ];
    private lastCatchFishSoundPlayed: UCSoundNames = UCSoundNames.WILD_CATCH_FISH_01;
    private separatorsSpine: AbstractSpine;
    private beerCanSpine: AbstractSpine;

    private isIdleActive: boolean = true;
    private beerCanIdleTween: gsap.core.Tween;
    private separatorsIdleTween: gsap.core.Tween;

    private reelsContainerMask: Container;
    private reelsContainer: Container;

    private logoLandscape: Sprite;
    private logoPortrait: Sprite;

    constructor() {
        super(`UCReelsView`);

        this.currentReels = [];
    }

    protected initGraphics(): void {
        this.reelsContainer = new Container();
        this.reelsContainer.name = `ReelsContainer`;
        this.reelsContainer.position.set(reelsConfig.reelsContainerX, reelsConfig.reelsContainerY);

        this.addChild(this.reelsContainer);
        this.addReelsMask();

        this.separatorContainer = new Container();
        this.separatorContainer.name = `SeparatorContainer`;
        this.separatorContainer.y = 115;
        this.addChild(this.separatorContainer);

        this.separatorsSpine = new AbstractSpine(UCSpineNames.REEL_SEPARATORS);
        this.separatorsSpine.scale.set(0.806, 0.8);
        this.separatorsSpine.x = 368;
        this.separatorsSpine.y = 311;

        this.separatorContainer.addChild(this.separatorsSpine);

        const textureArray = [];

        for (let i = 0; i <= 30; i++) {
            const texture = Texture.from(
                i < 10
                    ? `xbet_highlight/xbet_highlight_00${i}`
                    : `xbet_highlight/xbet_highlight_0${i}`
            );
            textureArray.push(texture);
        }

        this.xBetFrame = new AnimatedSprite(textureArray);
        this.xBetFrame.name = `xBetFrame`;
        this.xBetFrame.animationSpeed = 0.7;
        this.xBetFrame.scale.x = 0.99;

        this.xBetFrame.position.set(145.5, 116);

        if (devDebugConfig.showReelDebugGraphics) {
            if (devDebugConfig.disableReelMask) {
                this.scale.set(0.6, 0.6);
                this.position.set(184, 300);
            } else {
                this.scale.set(1, 1);
                this.position.set(reelsConfig.viewXPosition, reelsConfig.viewYPosition);
            }
        } else {
            this.scale.set(1, 1);
            this.position.set(reelsConfig.viewXPosition, reelsConfig.viewYPosition);
        }

        this.blockersContainer = new Container();
        this.blockersContainer.name = `ReelsBlockersContainer`;
        this.blockersContainer.x = reelsConfig.reelsContainerX + 2;
        this.blockersContainer.y = reelsConfig.reelsContainerY;

        this.fsMultiplier = new UCFreespinMultiplier();
        this.fsMultiplier.position.set(364, 126);
        this.addChild(this.fsMultiplier);

        this.beerCanSpine = new AbstractSpine(UCSpineNames.BEER_CAN);
        this.beerCanSpine.position.set(364, 136);
        this.addChild(this.beerCanSpine);

        this.logoLandscape = generateScaledSprite(`logoLandscape`);
        this.logoLandscape.scale.set(0.4);
        this.logoLandscape.anchor.set(0.5, 0.5);

        this.logoPortrait = generateScaledSprite(`logoPortrait`);
        this.logoPortrait.scale.set(0.4);
        this.logoPortrait.anchor.set(0.5, 0.5);

        this.checkLogoPosition();
    }

    protected isMobile(): boolean {
        return NolimitApplication.apiPlugin.options.device === Device.MOBILE;
    }

    public onResize(): void {
        this.checkLogoPosition();
    }

    private checkLogoPosition(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;

        if (NolimitApplication.isLandscape) {
            if (this.isMobile()) {
                this.logoLandscape.position.set(-bounds.width / 2 + 480, 175);
            } else {
                this.logoLandscape.position.set(-130, 175);
            }

            this.removeChild(this.logoPortrait);
            this.addChild(this.logoLandscape);
        } else {
            this.logoPortrait.position.set(bounds.width / 2, -bounds.height / 2 + 440);

            this.removeChild(this.logoLandscape);
            this.addChild(this.logoPortrait);
        }
    }

    protected addReelsMask() {
        this.reelsContainerMask = new Container();
        this.reelsContainerMask.name = `ReelsContainerMask`;

        if (devDebugConfig.disableReelMask) {
            return;
        }

        const reelsContainerMaskGraphics: Graphics = new Graphics();
        reelsContainerMaskGraphics.beginFill(0x00ff00, 0.5);
        reelsContainerMaskGraphics.drawRect(
            0,
            0,
            displayConfig.reelsViewWidth + 20,
            reelsConfig.rowsNumber * (symbolConfig.symbolHeight + symbolConfig.symbolSpacing) - 4
        );
        reelsContainerMaskGraphics.endFill();

        this.reelsContainerMask.position.set(-10, reelsConfig.reelsContainerY + 2);

        this.reelsContainerMask.addChild(reelsContainerMaskGraphics);

        this.reelsContainer.mask = this.reelsContainerMask;

        this.addChild(this.reelsContainerMask);
    }

    public enableIdle() {
        this.isIdleActive = true;

        this.beerCanIdleTween = gsap.delayedCall(10, this.onBeerCanIdleRepeat.bind(this));
        this.separatorsIdleTween = gsap.delayedCall(6, this.onSeparatorsIdleRepeat.bind(this));
    }

    public disableIdle() {
        this.isIdleActive = false;

        this.beerCanIdleTween?.kill();
        this.separatorsIdleTween?.kill();
    }

    private onBeerCanIdleRepeat() {
        if (this.isIdleActive) {
            const onSpineComplete = () => {
                this.beerCanSpine.reset();
                this.removeChild(this.beerCanSpine);
                if (this.isIdleActive) {
                    this.beerCanIdleTween?.kill();
                    this.beerCanIdleTween = gsap.delayedCall(
                        getRandomBetween(10, 14, true),
                        this.onBeerCanIdleRepeat.bind(this)
                    );
                }
            };
            this.beerCanSpine.setSkinByName(`can${getRandomBetween(1, 3, true)}`);
            this.beerCanSpine.getSpineTween(
                `can_toss_${getRandomBetween(1, 3, true)}`,
                false,
                () => {
                    this.addChild(this.beerCanSpine);
                    playSound({ name: UCSoundNames.IDLE_BEER_CAN });
                },
                null,
                onSpineComplete
            );
        }
    }

    private onSeparatorsIdleRepeat() {
        if (this.isIdleActive) {
            const onSpineComplete = () => {
                this.separatorsSpine.reset();
                if (this.isIdleActive) {
                    this.separatorsIdleTween?.kill();
                    this.separatorsIdleTween = gsap.delayedCall(
                        getRandomBetween(6, 9, true),
                        this.onSeparatorsIdleRepeat.bind(this)
                    );
                }
            };
            this.separatorsSpine.getSpineTween(`reel_hooks`, false, null, null, onSpineComplete);
        }
    }

    protected onGameModeUpdate(): void {
        this.currentReels.forEach((reel) => {
            reel.gameMode = this.gameMode;
        });

        if (this.gameMode === AbstractGameMode.NORMAL) {
            this.fsMultiplier.reset();
        }
    }

    public generateInitialReels(symbols: string[][]): void {
        //this is pre rendering the symbols to avoid the first frame lag
        const pregeneratedSymbols: AbstractSymbol[] = [];

        for (const key in UCSymbolNames) {
            if (Object.prototype.hasOwnProperty.call(UCSymbolNames, key)) {
                const value = UCSymbolNames[key as keyof typeof UCSymbolNames];
                for (let i = 0; i < 3; i++) {
                    if (value !== UCSpecialSymbolNames.X) {
                        const symbol = getSymbolFromPool(value);

                        pregeneratedSymbols.push(symbol);
                    }
                }
            }
        }

        pregeneratedSymbols.forEach((symbol) => returnSymbolToPool(symbol));

        symbols.forEach((reel: string[], index) => {
            const ucReel = new UCReel(index, reel);
            ucReel.gameMode = this.gameMode;
            ucReel.x =
                (symbolConfig.symbolWidth + reelsConfig.reelSpacing) * ucReel.reelId +
                reelsConfig.reelSpacingOffset[ucReel.reelId];

            const topBlocker = new UCReelBlocker(true, ucReel);
            topBlocker.name = `topSymbolBlocker`;
            topBlocker.isOpenDataFromServer = UgliestCatch.model.data.extraPositionsOpen[index];
            topBlocker.x = (symbolConfig.symbolWidth + reelsConfig.reelSpacing) * index - 1;
            this.blockersContainer.addChild(topBlocker);

            const bottomBlocker = new UCReelBlocker(false, ucReel);
            bottomBlocker.name = `bottomSymbolBlocker`;
            bottomBlocker.isOpenDataFromServer = UgliestCatch.model.data.extraPositionsOpen[index];
            bottomBlocker.x = topBlocker.x;
            bottomBlocker.y = symbolConfig.symbolHeight * 4 + symbolConfig.symbolSpacing * 4 - 3;
            this.blockersContainer.addChild(bottomBlocker);

            ucReel.topBlocker = topBlocker;
            ucReel.bottomBlocker = bottomBlocker;
            this.currentReels.push(ucReel);

            this.reelsContainer.addChild(ucReel);

            const topBorder = new Sprite(Texture.from(`separator`));
            topBorder.name = `topBorder`;
            topBorder.anchor.set(0, 0.5);
            topBorder.x = topBlocker.x + 20;
            topBorder.y = 8;
            this.separatorContainer.addChildAt(topBorder, 0);

            const bottomBorder = new Sprite(Texture.from(`separator`));
            bottomBorder.name = `bottomBorder`;
            bottomBorder.anchor.set(0, 0.5);
            bottomBorder.x = topBorder.x;
            bottomBorder.y = 555;
            this.separatorContainer.addChildAt(bottomBorder, 0);
        });

        this.addChild(this.separatorContainer);
        this.addChild(this.blockersContainer);
        this.addChild(this.fsMultiplier);
    }

    public getReels(): AbstractReel[] {
        return this.currentReels;
    }

    public showXBetFrame() {
        this.xBetFrame.gotoAndPlay(0);
        this.addChild(this.xBetFrame);

        this.currentReels[1].topBlocker.onXbetEnabled();
        this.currentReels[1].bottomBlocker.onXbetEnabled();
    }

    public hideXBetFrame() {
        this.removeChild(this.xBetFrame);
        this.xBetFrame.stop();

        this.currentReels[1].topBlocker.onXbetDisabled();
        this.currentReels[1].bottomBlocker.onXbetDisabled();
    }

    public getBoatProgressionAnimation(
        progress: number,
        isRestore: boolean = false,
        wildSymbol: UCWildSymbol | undefined = undefined
    ): gsap.core.Tween | gsap.core.Timeline {
        const tl = gsap.timeline();

        if (isRestore) {
            return this.fsMultiplier.getWildToBoatAnimation(progress, isRestore);
        } else {
            if (wildSymbol !== undefined) {
                return this.fsMultiplier.getWildToBoatAnimation(progress, isRestore, wildSymbol);
            }
        }

        return tl;
    }

    public updateBoatSkinToFS() {
        this.fsMultiplier.changeToFSSkin();
    }

    public catchFish(
        fishSymbol: UCSymbol,
        wildSymbol: UCWildSymbol,
        reelId: number
    ): gsap.core.Timeline {
        const mainTimeline = gsap.timeline({
            onStart: () => {
                let randomSoundNumber = getRandomBetween(0, 2, true);
                let soundName = this.catchFishSoundsList[randomSoundNumber];
                if (this.lastCatchFishSoundPlayed === soundName) {
                    randomSoundNumber++;
                    if (randomSoundNumber > this.catchFishSoundsList.length - 1)
                        randomSoundNumber = 0;
                }
                soundName = this.catchFishSoundsList[randomSoundNumber];
                this.lastCatchFishSoundPlayed = soundName;

                playSoundOnce({
                    name: soundName,
                    stereo: davidPanningReelStop[reelId],
                    playOnceMinDuration: 1,
                });
            },
        });

        const fishName =
            fishSymbol.name === UCSpecialSymbolNames.BIG_FISH ||
            fishSymbol.name === UCSpecialSymbolNames.MYSTERY
                ? (fishSymbol as UCBigFishSymbol).revealedSymbolName
                : fishSymbol.name;

        const multiplier = fishSymbol instanceof UCMysterySymbol ? fishSymbol.multiplier : 1;

        for (let i = 0; i < multiplier; i++) {
            let boardSpine: AbstractSpine;

            if (fishName !== UCSpecialSymbolNames.F7) {
                boardSpine = new AbstractSpine(UCSpineNames.FS_FISH_1_6_BOARDS);
                boardSpine.setSkinByName(
                    `${fishMultipliersMap.get(fishName as UCSpecialSymbolNames)}x`
                );
            } else {
                boardSpine = new AbstractSpine(UCSpineNames.FS_FISH_7_BOARD);
            }

            let fishInitialPosition = this.toLocal(fishSymbol.getSpineGlobalPosition());

            if (fishSymbol instanceof UCMysterySymbol) {
                fishInitialPosition = this.toLocal(
                    fishSymbol.newSymbolClones[i].getGlobalPosition()
                );
                boardSpine.scale.set(fishSymbol.newSymbolClones[i].scale.x);

                fishInitialPosition.y -= boardSpine.height / 2 - 1;
            } else if (fishSymbol instanceof UCBigFishSymbol) {
                boardSpine.scale.set(0.48);

                fishInitialPosition.x += 4;
                fishInitialPosition.y -= boardSpine.height / 2 + 2;
            } else {
                fishInitialPosition.x -= 16;
                fishInitialPosition.y -= 25;
            }

            boardSpine.position.set(fishInitialPosition.x + 16, fishInitialPosition.y + 25);

            const wildPosition = this.toLocal(wildSymbol.getSpineGlobalPosition());
            wildPosition.x -= 4;
            wildPosition.y -= boardSpine.height / 2 - 34;

            const defaultDistance = 650;
            const dist = Math.sqrt(
                (wildPosition.x - boardSpine.x) ** 2 + (wildPosition.y - boardSpine.y) ** 2
            );
            let animationDuration = 0.5 + (0.8 * dist) / defaultDistance;

            const tl = gsap.timeline({
                onComplete: () => {
                    this.removeChild(boardSpine);
                    boardSpine.destroy();
                },
            });

            const moveBoardToWildTween = gsap.to(boardSpine, {
                duration: animationDuration,
                pixi: {
                    x: wildPosition.x + 6,
                    y: wildPosition.y + 23,
                    scale: 0.55,
                },
                rotation: -0.07,
                onStart: () => {
                    boardSpine.state.setAnimation(0, `multiplier_board`, true);
                    this.addChild(boardSpine);
                },
            });
            tl.add(moveBoardToWildTween);

            const scaleDownAndHideBoardTween = gsap.to(boardSpine, {
                pixi: { scale: 0.3, alpha: 0 },
                duration: animationDuration * 0.3,
                ease: `power1.in`,
                onStart: () => {
                    // stopSound({ name: UCSoundNames.WILD_GOLD_COLLECT });
                    const fishNumber = +fishName.replace(/^\D+/g, ``);
                    const boardSpineSoundName =
                        fishNumber >= 5
                            ? UCSoundNames.WILD_GOLD_COLLECT_BIG
                            : UCSoundNames.WILD_GOLD_COLLECT;

                    playSoundOnce({
                        name: boardSpineSoundName,
                        playOnceMinDuration: 0.8,
                    });
                },
            });
            tl.add(scaleDownAndHideBoardTween, animationDuration * 0.8);

            tl.add(wildSymbol.updateMultiplier(fishName as UCSpecialSymbolNames));

            mainTimeline.add(tl);
        }

        return mainTimeline;
    }

    public handleBoatToWildMultiplyAnimation(
        wildSymbols: UCWildSymbol[],
        multiplier: number,
        addSkip: Function,
        timeline: gsap.core.Timeline
    ): gsap.core.Timeline {
        const delay = multiplier > 0 ? 0.2 : 0;

        wildSymbols.forEach((wildSymbol, index) => {
            timeline.call(() => {
                addSkip(timeline);
            });
            timeline.add(
                this.fsMultiplier.getBoatToWildMultiplyAnimation(wildSymbol, multiplier),
                `>+=${delay}`
            );
            timeline.addLabel(`boatToWild_${index}`, `>-=0.2`);
        });

        return timeline;
    }
}
